// src/components/Dashboard/AvailabilityCalendar.js

import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { firestore } from '../../firebase/config';
import './AvailabilityCalendar.css';

const AvailabilityCalendar = () => {
  const { userProfile } = useContext(AuthContext);
  const [vacations, setVacations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // État du formulaire pour les absences
  const [vacationData, setVacationData] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(new Date().setDate(new Date().getDate() + 7)).toISOString().split('T')[0],
    reason: '',
    isFullDay: true,
    startTime: '09:00',
    endTime: '18:00'
  });
  
  // Charger les absences du praticien
  useEffect(() => {
    const fetchVacations = async () => {
      if (!userProfile?.practitionerId) return;
      
      setLoading(true);
      
      try {
        // Récupérer les absences
        const vacationSnapshot = await firestore
          .collection('vacations')
          .where('practitionerId', '==', userProfile.practitionerId)
          .orderBy('startDate', 'desc')
          .get();
        
        // Organiser les absences
        const vacationList = vacationSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setVacations(vacationList);
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des absences:', error);
        setLoading(false);
      }
    };
    
    fetchVacations();
  }, [userProfile]);
  
  // Formater la date pour l'affichage
  const formatDate = (dateString) => {
    const options = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };
  
  // Mettre à jour les champs du formulaire d'absences
  const handleVacationInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setVacationData({
      ...vacationData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Sauvegarder les absences
  const handleSaveVacation = async (e) => {
    e.preventDefault();
    
    if (!userProfile?.practitionerId) return;
    
    // Vérifier que la date de début est antérieure à la date de fin
    const startDate = new Date(vacationData.startDate);
    const endDate = new Date(vacationData.endDate);
    
    if (startDate > endDate) {
      alert('La date de début doit être antérieure à la date de fin');
      return;
    }
    
    // Validation supplémentaire pour les heures si pas journée complète
    if (!vacationData.isFullDay) {
      if (vacationData.startTime >= vacationData.endTime) {
        alert("L'heure de début doit être antérieure à l'heure de fin");
        return;
      }
    }
    
    try {
      // Ajouter la période d'absences
      const vacationDataToSave = {
        practitionerId: userProfile.practitionerId,
        startDate: vacationData.startDate,
        endDate: vacationData.endDate,
        reason: vacationData.reason,
        isFullDay: vacationData.isFullDay,
        createdAt: new Date()
      };
      
      // Ajouter les heures uniquement si pas journée complète
      if (!vacationData.isFullDay) {
        vacationDataToSave.startTime = vacationData.startTime;
        vacationDataToSave.endTime = vacationData.endTime;
      }
      
      await firestore.collection('vacations').add(vacationDataToSave);
      
      // Recharger les absences
      const vacationSnapshot = await firestore
        .collection('vacations')
        .where('practitionerId', '==', userProfile.practitionerId)
        .orderBy('startDate', 'desc')
        .get();
      
      const vacationList = vacationSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setVacations(vacationList);
      
      // Réinitialiser le formulaire
      setVacationData({
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(new Date().setDate(new Date().getDate() + 7)).toISOString().split('T')[0],
        reason: '',
        isFullDay: true,
        startTime: '09:00',
        endTime: '18:00'
      });
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des absences:', error);
    }
  };
  
  // Supprimer un congé
  const handleDeleteVacation = async (vacationId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette période d'absences ?")) {
      return;
    }
    
    try {
      await firestore.collection('vacations').doc(vacationId).delete();
      
      // Mettre à jour l'état local
      setVacations(vacations.filter(v => v.id !== vacationId));
    } catch (error) {
      console.error('Erreur lors de la suppression du congé:', error);
    }
  };
  
  if (loading) {
    return (
      <div className="availability-calendar-loading">
        <div className="loading-spinner"></div>
        <p>Chargement des absences...</p>
      </div>
    );
  }
  
  return (
    <div className="availability-calendar">
      <div className="vacation-section-title">
        <h2>Gestion des absences</h2>
        <p className="section-description">
          Définissez vos périodes d'absences pour empêcher les réservations pendant votre absence. 
          Vous pouvez bloquer des journées entières ou seulement certaines plages horaires.
        </p>
      </div>
      
      <div className="vacation-layout">
        {/* Colonne gauche - Liste des absences */}
        <div className="vacation-list-section">
          <h3>Périodes d'absences</h3>
          
          {vacations.length === 0 ? (
            <div className="no-data">
              Aucune période d'absences définie.
            </div>
          ) : (
            <div className="vacations-list">
              {vacations.map(vacation => (
                <div key={vacation.id} className="vacation-item">
                  <div className="vacation-dates">
                    <strong>Du {formatDate(vacation.startDate)}</strong>
                    <strong> au {formatDate(vacation.endDate)}</strong>
                  </div>
                  
                  <div className="vacation-time">
                    {vacation.isFullDay ? (
                      <span className="full-day-badge">Journées complètes</span>
                    ) : (
                      <span className="time-range">De {vacation.startTime} à {vacation.endTime}</span>
                    )}
                  </div>
                  
                  {vacation.reason && (
                    <div className="vacation-reason">
                      Motif: {vacation.reason}
                    </div>
                  )}
                  
                  <div className="vacation-actions">
                    <button 
                      type="button" 
                      className="btn-icon btn-danger"
                      onClick={() => handleDeleteVacation(vacation.id)}
                      title="Supprimer ce congé"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Colonne droite - Formulaire d'ajout */}
        <div className="vacation-form-section">
          <h3>Déclarer une période d'absences</h3>
          
          <form onSubmit={handleSaveVacation}>
            <div className="form-group">
              <label>Date de début</label>
              <input
                type="date"
                name="startDate"
                value={vacationData.startDate}
                onChange={handleVacationInputChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label>Date de fin</label>
              <input
                type="date"
                name="endDate"
                value={vacationData.endDate}
                onChange={handleVacationInputChange}
                required
              />
            </div>
            
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="isFullDay"
                  checked={vacationData.isFullDay}
                  onChange={handleVacationInputChange}
                />
                Journées complètes
              </label>
              <div className="checkbox-hint">
                Si décoché, vous pourrez spécifier une plage horaire pour chaque jour de la période.
              </div>
            </div>
            
            {!vacationData.isFullDay && (
              <div className="form-row">
                <div className="form-group">
                  <label>Heure de début</label>
                  <input
                    type="time"
                    name="startTime"
                    value={vacationData.startTime}
                    onChange={handleVacationInputChange}
                    required={!vacationData.isFullDay}
                  />
                </div>
                
                <div className="form-group">
                  <label>Heure de fin</label>
                  <input
                    type="time"
                    name="endTime"
                    value={vacationData.endTime}
                    onChange={handleVacationInputChange}
                    required={!vacationData.isFullDay}
                  />
                </div>
              </div>
            )}
            
            <div className="form-group">
              <label>Motif (optionnel)</label>
              <input
                type="text"
                name="reason"
                value={vacationData.reason}
                onChange={handleVacationInputChange}
                placeholder="Vacances, formation, etc."
              />
            </div>
            
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                Ajouter cette période
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityCalendar;